// Spaced review + delayed-review evaluation + mastery decay. Phase D.
//
// Spec §"Delayed review and spacing logic" (lines 1046–1170).
// V1 ships a global review spine (0, 1, 3, 7, 21, 45, 90 days);
// the spec refines per mastery class. Phase D implements:
//   - REVIEW_SCHEDULES table per spec
//   - scheduleDelayedReview: returns the next review ISO timestamp
//     when a skill reaches trueMastery
//   - evaluateDelayedReview: scores delayed-review attempts to
//     produce a DelayedReviewEvidence shape
//   - detectMasteryDecay: triggers reactivation when a skill that
//     held in isolation drops in transfer form, or supports start
//     rising
//
// spacedReviewSchedule.json is empty in Phase D (§D-Q1). The
// REVIEW_SCHEDULES constant is the live schedule; the manifest is
// reserved for future per-skill overrides authored in expert review.

import type {
  ChildAttempt,
  DecisionBlockerCode,
  DecisionReasonCode,
  DelayedReviewEvidence,
  HintLevel,
  MasteryClass,
  SkillMasteryState,
} from "./types";
import { canCountTowardMastery, revealPrimedCorrectAttempts } from "./supportModeGating";
import { inferChannel } from "./masteryClassInference";
import {
  requiresConnectedTextTransfer,
  requiresWordProblemTransfer,
} from "./transferGating";

const HINT_ORDER: Record<HintLevel, number> = {
  H0: 0,
  H1: 1,
  H2: 2,
  H3: 3,
  H4: 4,
};

interface ReviewScheduleRule {
  firstReviewDelayDays: number;
  laterReviewDelayDays: number[];
  masteryDecaySignal: string;
  reactivationRule: string;
}

export const REVIEW_SCHEDULES: Record<MasteryClass, ReviewScheduleRule> = {
  awareness: {
    firstReviewDelayDays: 1,
    laterReviewDelayDays: [3, 7, 14],
    masteryDecaySignal: "fresh oral item fails or requires >H1 after one week",
    reactivationRule: "one easier oral item, one bridge item, one transfer item",
  },
  directPractice: {
    firstReviewDelayDays: 1,
    laterReviewDelayDays: [3, 7, 21],
    masteryDecaySignal: "accuracy drops on a fresh direct-practice item or supports rise",
    reactivationRule: "one easier direct item, one fresh same-level item, one retest",
  },
  strategyTransfer: {
    firstReviewDelayDays: 1,
    laterReviewDelayDays: [3, 7, 21, 45],
    masteryDecaySignal: "direct form holds but transfer form drops",
    reactivationRule: "restore the bridge representation, then retry transfer",
  },
  connectedText: {
    firstReviewDelayDays: 1,
    laterReviewDelayDays: [3, 7, 14, 30],
    masteryDecaySignal: "isolated items hold but sentence/passage accuracy drops",
    reactivationRule: "walk back to phrase/sentence support, then fresh connected-text retry",
  },
  durableMastery: {
    firstReviewDelayDays: 7,
    laterReviewDelayDays: [21, 45, 90],
    masteryDecaySignal: "multiple delayed-review misses or recurrent same-error pattern",
    reactivationRule: "review, then transfer, then delayed review again",
  },
};

/** Schedule the next delayed review for a skill that has reached
 * true mastery. Returns null when the skill is not yet true-mastered
 * (no review scheduled until mastery is achieved). */
export function scheduleDelayedReview(
  _skillId: string,
  masteryState: SkillMasteryState,
  nowIso: string = new Date().toISOString(),
): string | null {
  if (!masteryState.trueMastery) return null;
  const schedule = REVIEW_SCHEDULES[masteryState.masteryClass];
  const now = new Date(nowIso);
  const first = new Date(now);
  first.setDate(now.getDate() + schedule.firstReviewDelayDays);
  return first.toISOString();
}

/** Local calendar-day key (YYYY-MM-DD, local TZ) — same cheap semantics as
 * progression.ts's private `dayKey` (kept local: adaptation (L2) must not
 * import lib/engine (L3) under the one-way layering rule; the helper is 5
 * lines of pure date formatting, not a second home for a concern). */
function dayKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Evaluate delayed-review evidence from the attempt log. Filters to
 * attempts where `isDelayedReview === true`; scores via Phase C's
 * canCountTowardMastery for the skill's primary channel.
 *
 * MA-295 CROSS-DAY STRENGTHENING ("Mastered = confirmed on a later day",
 * MA-DEC-0721): a review attempt passes ONLY if it happened on a LATER
 * calendar day than some earlier practice of the same skill. Without this,
 * a review-slot serve in a second session the SAME day (pickReviewSkill's
 * longest-gap tier can select a skill secured earlier that day) minted
 * `isDelayedReview` evidence with zero cross-day signal — and since
 * trueMastery now keys off this evidence (masteryEvidence.ts MA-295), the
 * parent-facing "Mastered" claim would have been earnable same-day, the
 * exact over-claim the model change exists to close. */
export function evaluateDelayedReview(
  skillId: string,
  attempts: ChildAttempt[],
): DelayedReviewEvidence {
  const channel = inferChannel(skillId);
  const skillAttempts = attempts.filter((a) => a.skillId === skillId);
  const reviewAttempts = skillAttempts.filter((a) => a.isDelayedReview);
  const epoch = (iso: string): number => {
    const t = Date.parse(iso);
    return Number.isNaN(t) ? 0 : t;
  };
  // Cross-day: some earlier attempt of THIS skill sits on an earlier
  // calendar day — i.e. the review is a genuinely different-day retrieval,
  // not a later-same-day re-ask.
  // Session anchor (RED-review fix, aligns with MA-297's t_reach rule): a
  // review whose OWN session began on an earlier calendar day (a sitting
  // spanning local midnight) is a ~minutes-later re-ask, not a later-day
  // retrieval — it must not pass. sessionKey mirrors progression.ts's
  // convention (sessionId ?? runId ?? per-timestamp fallback).
  const sessionKeyOf = (a: ChildAttempt): string =>
    a.sessionId ?? a.runId ?? `t:${a.timestamp}`;
  const isCrossDayReview = (r: ChildAttempt): boolean => {
    const spansEarlierDay = skillAttempts.some(
      (p) =>
        sessionKeyOf(p) === sessionKeyOf(r) &&
        dayKey(p.timestamp) < dayKey(r.timestamp),
    );
    if (spansEarlierDay) return false;
    return skillAttempts.some(
      (p) =>
        epoch(p.timestamp) < epoch(r.timestamp) &&
        dayKey(p.timestamp) < dayKey(r.timestamp),
    );
  };
  // ⭐MA-588 (transfer-spaced-same-item) — a review-slot correct on an item
  // whose answer was already revealed to this child (e.g. IDK on X
  // yesterday, X re-served as today's review) is recall of the reveal, not
  // a later-day retrieval of the skill: it cannot pass delayed review. Same
  // predicate/definition as computeSkillMasteryState's counted-evidence bar,
  // scanned over the full skill-scoped lookback this function already
  // receives (no new plumbing). The row still stays in `reviewAttempts`
  // (occupies its slot; decay detection and lastReviewTimestamp unchanged).
  const revealPrimed = revealPrimedCorrectAttempts(skillAttempts);
  const independentPass = reviewAttempts.some(
    (a) =>
      a.correct &&
      !revealPrimed.has(a) &&
      canCountTowardMastery(a, channel) &&
      isCrossDayReview(a),
  );
  const failedReviewCount = reviewAttempts.filter((a) => !a.correct).length;
  const decayDetected = detectMasteryDecay(skillId, reviewAttempts);

  const reasonCodes: DecisionReasonCode[] = [];
  const blockerCodes: DecisionBlockerCode[] = [];

  if (independentPass) reasonCodes.push("DELAYED_REVIEW_PASSED");
  if (!independentPass) {
    blockerCodes.push("BLOCK_NO_DELAYED_REVIEW");
    reasonCodes.push("DELAYED_REVIEW_REQUIRED");
  }

  return {
    skillId,
    required: true,
    dueAt: null,
    reviewAttempts,
    passed: independentPass,
    lastReviewTimestamp:
      reviewAttempts.length > 0
        ? reviewAttempts[reviewAttempts.length - 1].timestamp
        : null,
    failedReviewCount,
    decayDetected,
    nextReviewDueAt: null,
    reasonCodes,
    blockerCodes,
  };
}

/** Detect mastery decay signals from a recent-attempt window.
 * Three signals:
 *   (a) connected-text skill: isolated items hold but text drops
 *   (b) strategy-transfer skill: equation holds but word-problem drops
 *   (c) generic: any attempt with hintLevel > H1 OR frustration_exit
 *
 * CALIBRATION-TARGET (Phase D §D-Q2 verbatim port + flagging):
 * signal (c) is sensitive — a SINGLE attempt above H1 or with
 * frustration_exit trips decay. This can over-trigger reactivation
 * for skills mid-acquisition. Phase F may filter further; for now
 * the spec's broad signal stands. Tune from telemetry when
 * acceleration rules + decision engine land.
 */
export function detectMasteryDecay(
  skillId: string,
  recentAttempts: ChildAttempt[],
): boolean {
  if (recentAttempts.length === 0) return false;

  if (requiresConnectedTextTransfer(skillId)) {
    const isolatedStable = recentAttempts.some(
      (a) => a.itemType === "directPractice" && a.correct,
    );
    const textDropped = recentAttempts.some(
      (a) => a.itemType === "connectedText" && !a.correct,
    );
    if (isolatedStable && textDropped) return true;
  }

  if (requiresWordProblemTransfer(skillId)) {
    const equationStable = recentAttempts.some(
      (a) => a.itemType === "directPractice" && a.correct,
    );
    const wordProblemDropped = recentAttempts.some(
      (a) => a.itemType === "wordProblem" && !a.correct,
    );
    if (equationStable && wordProblemDropped) return true;
  }

  // Generic signal — CALIBRATION-TARGET, see header.
  return recentAttempts.some(
    (a) =>
      HINT_ORDER[a.hintLevel] > HINT_ORDER.H1 ||
      a.errorType === "frustration_exit",
  );
}
